import { type Material, type Mesh, MeshBuilder, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import type { Cue } from "../../audio/engine.ts";
import * as loops from "../../audio/cues/loops.ts";
import { bell, click, fm, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow as glowMat } from "../../game/materials.ts";
import type { ClientHero } from "../types.ts";

const V = { dash: 0, pounce: 1, wall: 2, low: 3 } as const;
const PK = { Spear: pk(HeroId.BlackPanthera, 0) } as const;
const FX = { Claw: fxk(HeroId.BlackPanthera, 0), Spear: fxk(HeroId.BlackPanthera, 1), Pounce: fxk(HeroId.BlackPanthera, 2) } as const;
const D4 = 293.66;
const hz = (s: number) => D4 * 2 ** (s / 12);

const clawCue: Cue = (o) => {
  click(o, 0.18, 5200, 0.003);
  whoosh(o, 0.28, 900, 3600, 0.16);
  fm(o, { f: 950, ratio: 2.1, index: 3.5, index1: 0.2, gain: 0.08, d: 0.18 });
  return 0.28;
};
const spearCue: Cue = (o) => {
  whoosh(o, 0.42, 600, 2500, 0.28);
  tone(o, { f: hz(19), f1: hz(31), glide: 0.2, gain: 0.07, d: 0.22 });
  return 0.4;
};
const pounceCue: Cue = (o) => {
  thump(o, 0.45, 110, 55, 0.18);
  noise(o, { color: "pink", type: "bandpass", f: 500, f1: 2400, gain: 0.45, d: 0.35 });
  bell(o, hz(12), 0.35, 0.45);
  return 0.75;
};

const theme = {
  bpm: 132,
  octave: 0,
  voice: bell,
  notes: [
    [0, 0.5],
    [2, 0.5],
    [7, 0.75],
    [12, 1.25],
  ],
} satisfies ClientHero["audio"]["theme"];

type PantheraGear = { claws: Mesh[][]; ring: Mesh; clawMat: Material; eyes: Mesh[] };

const hero: ClientHero<PantheraGear> = {
  id: HeroId.BlackPanthera,
  bulk: 0.95,
  shape: { head: 0.95, torso: 0.9, arms: 1.05, legs: 1.08 },
  gear(g) {
    const { scene, rig, add, addWorld, glow, dark } = g;
    const clawMat = glow("#bca8ff", 0.85);
    const silver = glow("#d9d8e2", 0.72);
    const violet = glow("#9b7cff", 0.58);
    const claws = [rig.handL, rig.handR].map((hand, h) =>
      [-0.09, 0, 0.09].map((x) => {
        const c = add(MeshBuilder.CreateBox(`panthera-claw-${h}`, { width: 0.025, height: 0.025, depth: 0.46 }, scene), hand, clawMat);
        c.position.set(x, -0.02, -0.28);
        return c;
      }),
    );
    const eyes = [-1, 1].map((side) => {
      const eye = add(MeshBuilder.CreateBox("silver-eye-slash", { width: 0.18, height: 0.035, depth: 0.025 }, scene), rig.head, silver);
      eye.position.set(side * 0.105, 0.055, -0.235);
      eye.rotation.z = -side * 0.16;
      return eye;
    });
    for (const side of [-1, 1]) {
      const cheek = add(MeshBuilder.CreateBox("mask-cheek-trim", { width: 0.025, height: 0.18, depth: 0.025 }, scene), rig.head, silver);
      cheek.position.set(side * 0.19, -0.08, -0.22);
      cheek.rotation.z = side * 0.38;
    }
    for (const side of [-1, 1]) {
      const ear = add(MeshBuilder.CreateCylinder("panthera-ear", { diameterTop: 0, diameterBottom: 0.18, height: 0.28, tessellation: 4 }, scene), rig.head, dark);
      ear.position.set(side * 0.15, 0.24, 0.02);
      ear.rotation.z = -side * 0.28;
      const ankle = add(MeshBuilder.CreateTorus("ankle-vibe", { diameter: 0.26, thickness: 0.025, tessellation: 16 }, scene), side < 0 ? rig.footL : rig.footR, glow("#8d6bff", 0.55));
      ankle.rotation.x = Math.PI / 2;
      ankle.position.y = 0.05;
    }
    const collar = add(MeshBuilder.CreateTorus("panthera-collar", { diameter: 0.55, thickness: 0.035, tessellation: 24 }, scene), rig.torso, glow("#8d6bff"));
    collar.rotation.x = Math.PI / 2;
    collar.position.y = 0.23;
    for (let i = 0; i < 7; i++) {
      const a = -Math.PI * 0.78 + i * (Math.PI * 0.26);
      const bead = add(MeshBuilder.CreateSphere("silver-necklace-bead", { diameter: i === 3 ? 0.09 : 0.055, segments: 8 }, scene), rig.torso, i === 3 ? violet : silver);
      bead.position.set(Math.sin(a) * 0.28, 0.15 - Math.cos(a) * 0.1, -0.25 - Math.cos(a) * 0.06);
    }
    const ring = addWorld(MeshBuilder.CreateTorus("pounce-ring", { diameter: 2, thickness: 0.05, tessellation: 32 }, scene), glow("#ede9ff", 0.3));
    ring.setEnabled(false);
    return { claws, ring, clawMat, eyes };
  },
  animate(gear, f, _rig) {
    const flare = Math.max(f.v[V.dash] ?? 0, f.v[V.pounce] ?? 0, f.v[V.low] ? 0.35 : 0);
    for (const group of gear.claws) for (const c of group) c.scaling.z = 1 + flare * 0.8;
    for (const eye of gear.eyes) eye.scaling.x = 1 + flare * 0.25;
    gear.ring.setEnabled((f.v[V.pounce] ?? 0) > 0.05);
    gear.ring.position.set(f.pos.x, f.pos.y + 0.08, f.pos.z);
    gear.ring.scaling.setAll(1 + (1 - (f.v[V.pounce] ?? 0)) * 4);
    gear.ring.rotation.y = f.t * 6;
    if (f.action === 3 || f.moveKind >= 16) return "attack-melee-right";
    if (f.speed > 6.5) return "sprint";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Low fury", value: hv(c, eid, V.low), color: "#ede9ff" },
      { label: "Wall step", value: hv(c, eid, V.wall), color: "#8d6bff" },
    ];
  },
  projectiles: {
    [PK.Spear](scene, _frame) {
      const mesh = MeshBuilder.CreateCylinder("panthera-spear", { diameter: 0.08, height: 0.9, tessellation: 6 }, scene);
      mesh.material = glowMat(scene, "#9b7cff");
      mesh.rotation.x = Math.PI / 2;
      return {
        gear: null,
        update(_gear, f) {
          mesh.position.set(f.pos.x, f.pos.y, f.pos.z);
          mesh.lookAt(new Vector3(f.pos.x + f.dir.x, f.pos.y + f.dir.y, f.pos.z + f.dir.z));
          mesh.rotation.x += Math.PI / 2;
        },
        dispose: () => mesh.dispose(),
      };
    },
  },
  fx: {
    [FX.Claw]: {
      impact(c, pos) {
        const m = MeshBuilder.CreateTorus("claw-flash", { diameter: 0.8, thickness: 0.025, tessellation: 18 }, c.scene);
        m.material = c.glow("#bca8ff", 0.75);
        m.position.set(pos.x, pos.y, pos.z);
        c.timed(m, 0.16, (t, mesh) => mesh.scaling.set(1 + t, 1 + t, 1 + t));
      },
    },
    [FX.Spear]: { keepGeneric: true },
    [FX.Pounce]: { keepGeneric: true },
  },
  icons: {
    [Slot.Primary]: ["M5 19l5-14M11 19l3-14M17 19l2-14"],
    [Slot.Secondary]: ["M4 12h13M14 6l6 6-6 6", "M6 8l3 4-3 4"],
    [Slot.Ability1]: ["M4 18c6-8 10-10 16-12", "M12 6l5 1-2 4"],
    [Slot.Ability2]: ["M12 4a8 8 0 1 0 8 8", "M16 4l4 4-4 4"],
    [Slot.Ultimate]: ["M12 3l7 5v6l-7 7-7-7V8z", "M8 13l4-5 4 5"],
    passive: ["M4 17l5-10 3 4 4-7 4 13", "M5 20h14"],
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:black-panthera:claw", cue: trim(14, clawCue) }),
    ability: { [Slot.Secondary]: trim(14, spearCue), [Slot.Ability1]: trim(14, clawCue), [Slot.Ability2]: trim(14, clawCue), [Slot.Ultimate]: trim(5, pounceCue) },
    theme,
    themeTrim: { heroic: 1.5, hostile: -5.5, tease: 6.5 },
    loops: ({ v, alive }) => (alive && v[V.wall] > 0 ? [{ key: "panthera-wall", build: loops.climb, gain: 0.55 }] : []),
  },
};

void Btn;
export default hero;
